import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface PageContainerProps {
  /**
   * How wide the page may grow.
   *
   * `full` for boards and lists a supervisor scans across, `wide` for administration and lists with
   * many columns, `medium` for a record read top to bottom.
   */
  readonly width?: 'full' | 'wide' | 'medium';
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

const widths = {
  full: '',
  wide: 'max-w-(--width-container-wide)',
  medium: 'max-w-(--width-container-medium)',
} as const;

/**
 * The frame a page sits in: where it starts, how far it may stretch, and the space between its
 * sections.
 *
 * Every page had its own (M5.5-01): tickets edge to edge at one padding, Supervise at another,
 * administration centred with a third, so the heading jumped as a person moved between areas.
 * One frame makes the first thing on every page start in the same place.
 */
export function PageContainer({ width = 'wide', children, className }: PageContainerProps) {
  return (
    <div
      className={cn(
        'mx-auto flex w-full min-w-0 flex-col gap-(--space-lg)',
        'px-(--space-lg) py-(--space-xl) md:px-(--space-2xl)',
        widths[width],
        className,
      )}
    >
      {children}
    </div>
  );
}
